/**
 * Opens the images inside a mail body full size.
 *
 * A screenshot pasted into an Outlook mail arrives at the size the sender's own
 * window had — 1694px wide is an ordinary value — and the ticket panel is a
 * fraction of that. `.hit-message-body img` scales it down to fit, which keeps
 * the aspect ratio but leaves the thing it was a screenshot *of* unreadable.
 * This gives it somewhere to be read: an overlay showing the image at the size
 * the viewport allows, and a second click that drops it to its own pixels and
 * lets the overlay scroll.
 *
 * A delegated listener on the document rather than a handler per image, because
 * the thread appends messages after load (ToggleTicketMessageComponent) and the
 * user portal renders its own bodies — anything landing in a `.hit-message-body`
 * later is covered without a second pass.
 *
 * Plain DOM rather than HitModal: the bodies are server-rendered inside `v-pre`
 * precisely so Vue leaves the sender's markup alone, so there is no component
 * boundary here to hang a modal off.
 */

import { t } from '@/vue-components/ticket/SharedTicketFunctions.js'

/** Every wrapper that holds mail as the sender wrote it. */
const MAIL_BODY_SELECTOR = '.hit-message-body'

/** Above HitModal's z-[45], below the queue drawer's z-[100]. */
const OVERLAY_CLASSES =
  'fixed inset-0 z-[95] flex overflow-auto bg-gray-950/90 p-4'

/** Fits the whole picture in the overlay. `m-auto` centres it. */
const FIT_CLASSES = 'm-auto max-h-full max-w-full cursor-zoom-in object-contain'

/**
 * The image at its own pixel size, panned by scrolling the overlay.
 *
 * `m-auto` stays: centring an overflowing child with `justify-content` puts its
 * left edge outside the scrollable area, where nothing can reach it again.
 */
const ACTUAL_SIZE_CLASSES = 'm-auto max-h-none max-w-none cursor-zoom-out'

let overlay: HTMLElement | null = null
let listening = false
let bodyOverflowBefore = ''
let focusBefore: Element | null = null

/** Is this an image a mail body owns, as opposed to one of our own? */
function isMailImage(target: EventTarget | null): target is HTMLImageElement {
  return (
    target instanceof HTMLImageElement &&
    target.closest(MAIL_BODY_SELECTOR) !== null
  )
}

function toggleActualSize(image: HTMLImageElement): void {
  const zoomed = image.dataset.zoomed === 'true'

  image.className = zoomed ? FIT_CLASSES : ACTUAL_SIZE_CLASSES
  image.dataset.zoomed = zoomed ? 'false' : 'true'
}

/** Escape closes, from wherever the focus happens to be. */
function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    closeInlineImageViewer()
  }
}

export function closeInlineImageViewer(): void {
  if (!overlay) {
    return
  }

  overlay.remove()
  overlay = null

  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = bodyOverflowBefore

  if (focusBefore instanceof HTMLElement) {
    focusBefore.focus()
  }

  focusBefore = null
}

/**
 * Shows one image over the page.
 *
 * The source is taken from the element that was clicked, so an inline image
 * resolves through the same `/attachment/view` URL it already renders from and
 * nothing has to know how that URL is built.
 */
export function openInlineImageViewer(source: HTMLImageElement): void {
  closeInlineImageViewer()

  focusBefore = document.activeElement
  bodyOverflowBefore = document.body.style.overflow
  document.body.style.overflow = 'hidden'

  overlay = document.createElement('div')
  overlay.className = OVERLAY_CLASSES
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', source.alt || t('Image', 'Bild'))
  overlay.dataset.testid = 'inline-image-viewer'

  const image = document.createElement('img')
  image.src = source.currentSrc || source.src
  image.alt = source.alt
  image.className = FIT_CLASSES
  image.dataset.zoomed = 'false'
  image.addEventListener('click', function (event) {
    event.stopPropagation()
    toggleActualSize(image)
  })

  const close = document.createElement('button')
  close.type = 'button'
  close.className =
    'fixed top-4 right-4 rounded-full bg-gray-900/70 p-2 text-white hover:bg-gray-900 focus:ring-2 focus:ring-white focus:outline-hidden'
  close.setAttribute('aria-label', t('Close', 'Stäng'))
  close.dataset.testid = 'inline-image-viewer-close'
  close.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-6 w-6" aria-hidden="true">' +
    '<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>'
  close.addEventListener('click', closeInlineImageViewer)

  /* Anywhere but the picture itself closes, which is what a backdrop is for. */
  overlay.addEventListener('click', closeInlineImageViewer)

  overlay.append(image, close)
  document.body.append(overlay)
  document.addEventListener('keydown', handleKeydown)

  close.focus()
}

/**
 * A left click with no modifier, so ctrl-click and middle click still hand the
 * image to the browser's own menu, and a linked image still follows its link.
 */
function handleClick(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0) {
    return
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return
  }

  const image = event.target

  if (!isMailImage(image) || image.closest('a[href]')) {
    return
  }

  event.preventDefault()
  openInlineImageViewer(image)
}

/**
 * Registers the one listener the whole page needs, and says so on the root
 * element: a test that clicks an image and sees nothing happen needs to be able
 * to tell "the viewer is broken" from "this page never loaded it".
 */
export function enableInlineImageZoom(): void {
  if (listening) {
    return
  }

  document.addEventListener('click', handleClick)
  document.documentElement.dataset.inlineImageZoom = 'on'
  listening = true
}
